import apiClient from "../utils/apiClient";

// reusable error handler
const handleApiError = (error, defaultMessage) => {
  console.error(defaultMessage, error);

  throw {
    status: error.response?.status,
    message:
      error.response?.data?.message ||
      error.response?.data?.detail ||
      defaultMessage,
    data: error.response?.data || null,
  };
};



// Fetch subscriptions list
export const getSubscriptionsApi = async (page = 1, pageSize = 5) => {
  try {
    const token = sessionStorage.getItem("accessToken");

    const res = await apiClient.get("/subscriptions/", {
      params: { page, page_size: pageSize },
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
    });

    return res.data;
  } catch (error) {
    handleApiError(error, "Failed to fetch subscriptions.");
  }
};



// Fetch subscription dashboard stats
export const getSubscriptionDashboardApi = async () => {
  try {
    const token = sessionStorage.getItem("accessToken");

    const res = await apiClient.get("/dashboard/subscription_dashboard/", {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
    });

    return res.data;
  } catch (error) {
    handleApiError(error, "Failed to fetch dashboard stats.");
  }
};



// Fetch subscription plans
export const getSubscriptionPlansApi = async (search = "") => {
  try {
    const token = sessionStorage.getItem("accessToken");

    const response = await apiClient.get("/subscriptions/plans/", {
      params: { search },
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
    });

    return response.data.data;
  } catch (error) {
    handleApiError(error, "Failed to fetch subscription plans.");
  }
};



// Create subscription plan
export const createSubscriptionPlanApi = async (planData) => {
  try {
    const token = sessionStorage.getItem("accessToken");
    const formData = new FormData();

    const appendIfValid = (key, value) => {
      if (value == null || value === "" || value === undefined) return;

      if (
        [
          "price_monthly",
          "price_yearly",
          "max_projects",
          "max_client_admins",
          "max_project_admins",
          "max_surveyors",
          "overage_project_price",
          "overage_client_admin_price",
          "overage_project_admin_price",
          "overage_surveyor_price",
        ].includes(key)
      ) {
        const num = Number(value);
        if (!isNaN(num)) formData.append(key, num.toString());
        return;
      }

      formData.append(key, value);
    };

    Object.entries(planData).forEach(([k, v]) => appendIfValid(k, v));

    // Features JSON
    if (planData.features?.trim()) {
      try {
        const parsed = JSON.parse(planData.features);
        formData.append("features", JSON.stringify(parsed));
      } catch {
        throw { message: "Invalid features JSON format." };
      }
    }

    formData.append("allows_overage", planData.allows_overage ? "true" : "false");
    formData.append("is_active", planData.is_active ? "true" : "false");

    const response = await apiClient.post("/subscriptions/plans/", formData, {
      headers: { Authorization: `Bearer ${token}` },
    });

    return response.data;

  } catch (error) {
    handleApiError(error, "Failed to create subscription plan.");
  }
};



// Create subscription
export const createSubscriptionApi = async (subscriptionData) => {
  try {
    const token = sessionStorage.getItem("accessToken");
    const formData = new FormData();

    formData.append("organization", subscriptionData.organization);
    formData.append("plan", subscriptionData.plan);
    formData.append("status", subscriptionData.status);
    formData.append("billing_cycle", subscriptionData.billing_cycle);
    formData.append("start_date", subscriptionData.start_date);
    formData.append("end_date", subscriptionData.end_date);
    formData.append("next_billing_date", subscriptionData.next_billing_date);

    if (subscriptionData.payment_method)
      formData.append("payment_method", subscriptionData.payment_method);

    if (subscriptionData.payment_status)
      formData.append("payment_status", subscriptionData.payment_status);

    if (subscriptionData.auto_renew !== undefined)
      formData.append("auto_renew", subscriptionData.auto_renew.toString());

    const response = await apiClient.post("/subscriptions/", formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
    });

    return response.data;

  } catch (error) {
    handleApiError(error, "Failed to create subscription.");
  }
};



// Select plan
export const selectPlanApi = async (organizationId, planId) => {
  try {
    const response = await apiClient.post(
      `/subscriptions/${organizationId}/select-plan/`,
      { plan_id: planId }
    );

    return response.data;

  } catch (error) {
    handleApiError(error, "Failed to select plan.");
  }
};

// Fetch single subscription detail
export const getSubscriptionByIdApi = async (id) => {
  try {
    const token = sessionStorage.getItem("accessToken");

    const res = await apiClient.get(`/subscriptions/${id}/`, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
    });

    return res.data;
  } catch (error) {
    handleApiError(error, "Failed to fetch subscription detail.");
  }
};

// Update (PATCH) subscription
export const updateSubscriptionApi = async (id, subscriptionData) => {
  try {
    const token = sessionStorage.getItem("accessToken");
    const formData = new FormData();

    // Only append fields that are sent (PATCH → partial update)
    const optionalFields = [
      "plan", "status", "billing_cycle", "start_date", "end_date",
      "next_billing_date", "payment_method", "payment_status", "auto_renew",
      "extra_projects", "extra_client_admins", "extra_project_admins", "extra_surveyors"
    ];

    optionalFields.forEach(key => {
      if (subscriptionData[key] !== undefined && subscriptionData[key] !== "") {
        formData.append(key, subscriptionData[key]);
      }
    });

    const response = await apiClient.patch(`/subscriptions/${id}/`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        // Note: if your backend expects JSON, change to application/json + JSON.stringify
        // but since create uses FormData → keeping consistent
      },
    });

    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to update subscription.");
  }
};

// Delete subscription
export const deleteSubscriptionApi = async (id) => {
  try {
    const token = sessionStorage.getItem("accessToken");

    await apiClient.delete(`/subscriptions/${id}/`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return true;
  } catch (error) {
    handleApiError(error, "Failed to delete subscription.");
  }
};

// Get current user's subscription
export const getMySubscriptionApi = async () => {
  try {
    const token = sessionStorage.getItem("accessToken");

    const res = await apiClient.get(
      "/subscriptions/my_subscription/",
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      }
    );

    return res.data;
  } catch (error) {
    handleApiError(error, "Failed to fetch current subscription.");
  }
};